/**
 * 数创工坊相关功能
 * @description 提供数创工坊平台相关的功能接口
 */
import { djjenc } from '../crypto'

const url_shuchk='https://www.shuchk.com/shuchk';
// const url_shuchk=window.location.href;
const url_api='https://www.shuchk.com:8888';

/**
 * 生成唯一ID
 * @description 基于Unix时间戳和随机数生成唯一ID，格式：时间戳(毫秒) + 随机数(十六进制)
 * @param {number} randomLength - 随机数长度，默认为3，生成3位十六进制随机数
 * @returns {string} 唯一ID字符串
 * @example
 * PRL.generateUniqueId();
 * // 返回：'1729524572003D8A'
 * 
 * PRL.generateUniqueId(4);
 * // 返回：'1729524572003A2B4'
 */
export function generateUniqueId(randomLength: number = 3): string {
  const timestamp = Date.now();
  const random = Math.random().toString(16).substring(2, 2 + randomLength).toUpperCase();
  return timestamp.toString() + random;
}

/**
 * 获取数创工坊默认缩略图
 * @doc true
 * @description 返回数创工坊平台默认缩略图地址
 * @returns {string} 默认缩略图 URL
 * @example
 * const thumb = PRL.get_apple_project_thumb();
 * // 返回：https://www.shuchk.com/image/nopic.png
 */
export function get_apple_project_thumb(): string {
  return 'https://www.shuchk.com/image/nopic.png';
}

/**
 * 打开数创工坊项目
 * @doc true
 * @description 根据项目ID打开数创工坊编辑器
 * @param {string} id - 项目ID
 * @param {'redirect' | 'geturl'} mode - 模式：redirect(默认)打开新窗口，geturl返回URL字符串
 * @returns {void | string} mode为redirect时无返回值，mode为geturl时返回URL字符串
 * @example
 * // 模式1：redirect（默认），打开新窗口
 * PRL.openshuchk_project('177469772595');
 * // 会打开新窗口：https://www.shuchk.com/shuchk/#/editor?openshuchk_project=xxx&effectmode=performance
 *
 * // 模式2：geturl，返回URL字符串
 * const url = PRL.openshuchk_project('177469772595', 'geturl');
 * // 返回：'https://www.shuchk.com/shuchk/#/editor?openshuchk_project=xxx&effectmode=performance'
 */
export function openshuchk_project(id: string, mode: 'redirect' | 'geturl' = 'redirect'): void | string {
  const openshuchkProjectData = {
    id: id,
    session_id: "",
    timestamp: Math.floor(Date.now() / 1000)
  };
  const openshuchk_project_str = djjenc(JSON.stringify(openshuchkProjectData), 'v1');
  const url = `${url_shuchk}?openshuchk_project=${openshuchk_project_str}&effectmode=performance`;
  if (mode === 'geturl') {
    return url;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * 预览数创工坊项目
 * @doc true
 * @description 根据项目ID预览数创工坊项目
 * @param {string} id - 项目ID
 * @param {'redirect' | 'geturl'} mode - 模式：redirect(默认)打开新窗口，geturl返回URL字符串
 * @returns {void | string} mode为redirect时无返回值，mode为geturl时返回URL字符串
 * @example
 * // 模式1：redirect（默认），打开新窗口
 * PRL.viewshuchk_project('177469772595');
 * // 会打开新窗口：https://www.shuchk.com/shuchk/#/editor?viewshuchk_project=xxx&effectmode=performance
 *
 * // 模式2：geturl，返回URL字符串
 * const url = PRL.viewshuchk_project('177469772595', 'geturl');
 * // 返回：'https://www.shuchk.com/shuchk/#/editor?viewshuchk_project=xxx&effectmode=performance'
 */
export function viewshuchk_project(id: string, mode: 'redirect' | 'geturl' = 'redirect'): void | string {
  const projectData = {
    id: id,
    session_id: "",
    timestamp: Math.floor(Date.now() / 1000)
  };
  const editshuchk_project_str = djjenc(JSON.stringify(projectData), 'v1');
  const url = `${url_shuchk}?viewshuchk_project=${editshuchk_project_str}&effectmode=performance`;
  if (mode === 'geturl') {
    return url;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * 打开数创工坊案例
 * @doc true
 * @description 根据案例ID打开数创工坊编辑器
 * @param {string} id - 案例ID
 * @param {'redirect' | 'geturl'} mode - 模式：redirect(默认)打开新窗口，geturl返回URL字符串
 * @returns {void | string} mode为redirect时无返回值，mode为geturl时返回URL字符串
 * @example
 * // 模式1：redirect（默认），打开新窗口
 * PRL.openshuchk_case('177469772595');
 * // 会打开新窗口：https://www.shuchk.com/shuchk/#/editor?openshuchk_case=xxx&effectmode=performance
 *
 * // 模式2：geturl，返回URL字符串
 * const url = PRL.openshuchk_case('177469772595', 'geturl');
 * // 返回：'https://www.shuchk.com/shuchk/#/editor?openshuchk_case=xxx&effectmode=performance'
 */
export function openshuchk_case(id: string, mode: 'redirect' | 'geturl' = 'redirect'): void | string {
  const caseData = {
    id: id,
    session_id: "",
    timestamp: Math.floor(Date.now() / 1000)
  };
  const openshuchk_case_str = djjenc(JSON.stringify(caseData), 'v1');
  const url = `${url_shuchk}?openshuchk_case=${openshuchk_case_str}&effectmode=performance`;
  if (mode === 'geturl') {
    return url;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * 预览数创工坊案例
 * @doc true
 * @description 根据案例ID预览数创工坊案例
 * @param {string} id - 案例ID
 * @param {'redirect' | 'geturl'} mode - 模式：redirect(默认)打开新窗口，geturl返回URL字符串
 * @returns {void | string} mode为redirect时无返回值，mode为geturl时返回URL字符串
 * @example
 * // 模式1：redirect（默认），打开新窗口
 * PRL.viewshuchk_case('177469772595');
 * // 会打开新窗口：https://www.shuchk.com/shuchk/#/editor?viewshuchk_case=xxx&effectmode=performance
 *
 * // 模式2：geturl，返回URL字符串
 * const url = PRL.viewshuchk_case('177469772595', 'geturl');
 * // 返回：'https://www.shuchk.com/shuchk/#/editor?viewshuchk_case=xxx&effectmode=performance'
 */
export function viewshuchk_case(id: string, mode: 'redirect' | 'geturl' = 'redirect'): void | string {
  const caseData = {
    id: id,
    session_id: "",
    timestamp: Math.floor(Date.now() / 1000)
  };
  const viewshuchk_case_str = djjenc(JSON.stringify(caseData), 'v1');
  const url = `${url_shuchk}?viewshuchk_case=${viewshuchk_case_str}&effectmode=performance`;
  if (mode === 'geturl') {
    return url;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * 预览并购买数创工坊案例
 * @doc true
 * @description 根据案例ID打开案例购买预览页面
 * @param {string} id - 案例ID
 * @param {'redirect' | 'geturl'} mode - 模式：redirect(默认)打开新窗口，geturl返回URL字符串
 * @returns {void | string} mode为redirect时无返回值，mode为geturl时返回URL字符串
 * @example
 * // 模式1：redirect（默认），打开新窗口
 * PRL.viewshuchk_casebuy('177469772595');
 * // 会打开新窗口：https://www.shuchk.com/shuchk/#/editor?viewshuchk_casebuy=xxx&effectmode=performance
 *
 * // 模式2：geturl，返回URL字符串
 * const url = PRL.viewshuchk_casebuy('177469772595', 'geturl');
 * // 返回：'https://www.shuchk.com/shuchk/#/editor?viewshuchk_casebuy=xxx&effectmode=performance'
 */
export function viewshuchk_casebuy(id: string, mode: 'redirect' | 'geturl' = 'redirect'): void | string {
  const caseData = {
    id: id,
    session_id: "",
    timestamp: Math.floor(Date.now() / 1000)
  };
  const viewshuchk_casebuy_str = djjenc(JSON.stringify(caseData), 'v1');
  const url = `${url_shuchk}?viewshuchk_casebuy=${viewshuchk_casebuy_str}&effectmode=performance`;
  if (mode === 'geturl') {
    return url;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * 打开数创工坊模型
 * @doc true
 * @description 根据模型ID打开数创工坊编辑器
 * @param {string} id - 模型ID
 * @param {'redirect' | 'geturl'} mode - 模式：redirect(默认)打开新窗口，geturl返回URL字符串
 * @returns {void | string} mode为redirect时无返回值，mode为geturl时返回URL字符串
 * @example
 * // 模式1：redirect（默认），打开新窗口
 * PRL.openshuchk_model('177469772595');
 * // 会打开新窗口：https://www.shuchk.com/shuchk/#/editor?openshuchk_model=xxx&effectmode=performance
 *
 * // 模式2：geturl，返回URL字符串
 * const url = PRL.openshuchk_model('177469772595', 'geturl');
 * // 返回：'https://www.shuchk.com/shuchk/#/editor?openshuchk_model=xxx&effectmode=performance'
 */
export function openshuchk_model(id: string, mode: 'redirect' | 'geturl' = 'redirect'): void | string {
  const modelData = {
    id: id,
    session_id: "",
    timestamp: Math.floor(Date.now() / 1000)
  };
  const openshuchk_model_str = djjenc(JSON.stringify(modelData), 'v1');
  const url = `${url_shuchk}?openshuchk_model=${openshuchk_model_str}&effectmode=performance`;
  if (mode === 'geturl') {
    return url;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * 预览数创工坊模型
 * @doc true
 * @description 根据模型ID预览数创工坊模型
 * @param {string} id - 模型ID
 * @param {'redirect' | 'geturl'} mode - 模式：redirect(默认)打开新窗口，geturl返回URL字符串
 * @returns {void | string} mode为redirect时无返回值，mode为geturl时返回URL字符串
 * @example
 * // 模式1：redirect（默认），打开新窗口
 * PRL.viewshuchk_model('177469772595');
 * // 会打开新窗口：https://www.shuchk.com/shuchk/#/editor?viewshuchk_model=xxx&effectmode=performance
 *
 * // 模式2：geturl，返回URL字符串
 * const url = PRL.viewshuchk_model('177469772595', 'geturl');
 * // 返回：'https://www.shuchk.com/shuchk/#/editor?viewshuchk_model=xxx&effectmode=performance'
 */
export function viewshuchk_model(id: string, mode: 'redirect' | 'geturl' = 'redirect'): void | string {
  const modelData = {
    id: id,
    session_id: "",
    timestamp: Math.floor(Date.now() / 1000)
  };
  const viewshuchk_model_str = djjenc(JSON.stringify(modelData), 'v1');
  const url = `${url_shuchk}?viewshuchk_model=${viewshuchk_model_str}&effectmode=performance`;
  if (mode === 'geturl') {
    return url;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * 导入数创工坊项目
 * @doc true
 * @description 上传 .shuchk 文件并导入到数创工坊平台
 * @param {string} name - 项目名称
 * @param {string} shuchk_id - 用户ID
 * @param {Function} callback - 回调函数，接收上传结果
 * @returns {void}
 * @example
 * PRL.import_shuchk('新项目', 'casdoor_77c26ee6dca52dcdf9603dc0525cd9c9', (result) => {
 *   console.log('导入结果:', result);
 * });
 */
export function import_shuchk(name: string, shuchk_id: string, callback: (result: any) => void): void {
  // 创建文件选择输入
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.shuchk';
  
  // 监听文件选择
  input.addEventListener('change', async (e) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    
    if (!file) {
      callback({ success: false, message: '未选择文件' });
      return;
    }
    
    try {
      // 读取文件内容
      const content = await readFileAsText(file);
      
      // 发送POST请求
      const response = await fetch(`${url_api}/apple_project_create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          shuchk_id,
          project_json: content
        })
      });
      
      const result = await response.json();
      callback(result);
    } catch (error: any) {
      callback({ success: false, message: '导入失败', error: error.message });
    }
  });
  
  // 触发文件选择
  input.click();
}

/**
 * 读取文件为文本
 * @param {File} file - 文件对象
 * @returns {Promise<string>} 文件内容
 */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      resolve(content);
    };
    reader.onerror = (e) => {
      reject(new Error('文件读取失败'));
    };
    reader.readAsText(file);
  });
}

